import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, Loader2, SlidersHorizontal, Zap } from "lucide-react";
import { SectionHeading, StatTile, StepInstructions } from "@/components/wizard/ui";
import { FieldInfo } from "@/components/wizard/InfoTip";
import MacroChart from "@/components/wizard/MacroChart";
import LowEnergyWarning from "@/components/wizard/LowEnergyWarning";
import { MACRO_ICON } from "@/components/wizard/icons";
import api from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

export default function Step3Macros({ plan, patch }) {
  const { profile, goal, macros } = plan;
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTargets = useCallback(async (override) => {
    if (!profile.age || !profile.weight || !profile.height) return;
    setLoading(true);
    try {
      const { data } = await api.post("/engine/targets", {
        profile, trainingWeek: plan.trainingWeek, goal,
        macroOverride: override || (macros.mode === "manual" ? macros : null),
      });
      setTargets(data);
      if (macros.mode !== "manual") {
        patch("macros", { protein: data.autoMacros.protein, carb: data.autoMacros.carb, fat: data.autoMacros.fat });
      }
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [profile, goal, plan.trainingWeek, macros, patch]);

  useEffect(() => { fetchTargets(); /* eslint-disable-next-line */ }, []);

  const weight = parseFloat(profile.weight) || 0;
  const proteinG = Math.round(macros.protein * weight);
  const carbG = Math.round(macros.carb * weight);
  const fatG = Math.round(macros.fat * weight);

  const setMode = (mode) => {
    patch("macros", { mode });
    if (mode === "auto" && targets) {
      patch("macros", { mode, ...targets.autoMacros });
    }
  };

  const updateMacro = (key, val) => patch("macros", { [key]: val, mode: "manual" });

  return (
    <div>
      <SectionHeading eyebrow="Step 04" title="Macro & Target Engine"
        desc="BMR via Mifflin-St Jeor, scaled by your baseline activity. Accept the auto split or override per-kilo targets." />

      <StepInstructions>
        Review your calculated energy and macro targets. Leave it on <span className="text-[#E2E30A]">Auto</span> for our recommended split, or switch to <span className="text-[#E2E30A]">Manual</span> and drag the sliders to fine-tune protein, carbs and fat per kilo of bodyweight.
      </StepInstructions>

      {loading && !targets ? (
        <div className="flex items-center gap-2 text-zinc-200"><Loader2 className="h-4 w-4 animate-spin" /> Computing targets…</div>
      ) : !targets ? (
        <p className="text-zinc-200">Complete your profile in Step 1 to compute targets.</p>
      ) : (
        <>
          <LowEnergyWarning targets={targets} profile={profile} />
          <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatTile testId="stat-bmr" label="BMR" value={targets.bmr} unit="kcal"
                info={<FieldInfo testId="info-bmr" title="BMR" align="start">
                  Basal Metabolic Rate — the energy your body needs at complete rest, from the Mifflin-St Jeor equation using your sex, weight, height and age.
                </FieldInfo>} />
              <StatTile testId="stat-tdee" label="Baseline TDEE" value={targets.baselineTdee} unit="kcal" accent="#4C9AFF"
                info={<FieldInfo testId="info-tdee" title="Baseline TDEE" align="start">
                  Total Daily Energy Expenditure for everyday living = BMR × your non-training activity factor. This is your maintenance energy BEFORE any training — each day's session energy is added on top separately.
                </FieldInfo>} />
              <StatTile testId="stat-restenergy" label="Rest Day Energy" value={targets.restEnergy || "—"} unit="kcal" accent="#34C759"
                info={<FieldInfo testId="info-restenergy" title="Rest Day Energy" align="start">
                  Your target on a true no-training day. It sits at maintenance (Baseline TDEE) with a small difference only from macro rounding. Training days rise above this as fuelling carbs are added.
                </FieldInfo>} />
              <StatTile testId="stat-demand" label="Weekly Demand" value={targets.weeklyDemand} accent="#FF9500" dense />
            </div>

            <div className="border border-white/10 bg-[#141414] p-5">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-[#4C9AFF]" />
                  <span className="text-xs uppercase tracking-[0.2em] text-zinc-200 flex items-center gap-1.5">
                    Macro Targets (g / kg bodyweight)
                    <FieldInfo testId="info-macro-mode" title="Auto vs Manual" align="start">
                      <div><b className="text-white">Auto</b> — we set protein, carbs and fat from your profile, goal and each day's training demand (recommended).</div>
                      <div><b className="text-white">Manual</b> — drag the sliders to set your own grams per kg. Daily calories still flex with training load; your split is applied on top.</div>
                    </FieldInfo>
                  </span>
                </div>
                <div className="flex border border-white/10">
                  {["auto", "manual"].map((m) => (
                    <button key={m} data-testid={`macro-mode-${m}`} onClick={() => setMode(m)}
                      className={`px-3 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                        macros.mode === m ? "bg-[#E2E30A] text-black font-semibold" : "text-zinc-200 hover:text-white"}`}>{m}</button>
                  ))}
                </div>
              </div>

              {[
                { key: "protein", label: "Protein", color: "#4C9AFF", min: 1.2, max: 2.6, step: 0.1, g: proteinG },
                { key: "carb", label: "Carbohydrate", color: "#34C759", min: 2, max: 12, step: 0.5, g: carbG },
                { key: "fat", label: "Fat", color: "#FF9500", min: 0.5, max: 2, step: 0.1, g: fatG },
              ].map((row) => (
                <div key={row.key} className="mb-6 last:mb-0" data-testid={`macro-row-${row.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white tracking-wide flex items-center gap-2">
                      {React.createElement(MACRO_ICON[row.key], { className: "h-4 w-4", style: { color: row.color } })}
                      {row.label}
                    </span>
                    <span className="text-sm font-mono" style={{ color: row.color }}>
                      {macros[row.key].toFixed(1)} g/kg · <span className="text-white">{row.g}g</span>
                    </span>
                  </div>
                  <Slider data-testid={`macro-slider-${row.key}`}
                    value={[macros[row.key]]} min={row.min} max={row.max} step={row.step}
                    onValueChange={(v) => updateMacro(row.key, v[0])} className="[&_[role=slider]]:rounded-none" />
                </div>
              ))}

              <Button data-testid="macro-recalc-btn" variant="ghost" onClick={() => setMode("auto")}
                className="rounded-none text-[#4C9AFF] hover:bg-[#4C9AFF]/10 mt-2 uppercase text-xs tracking-wider">
                <RefreshCw className="h-3.5 w-3.5 mr-2" /> Reset to auto
              </Button>
            </div>
          </div>

          <div className="border border-white/10 bg-[#141414] p-6 flex flex-col items-center justify-center">
            <div className="flex items-center gap-2 mb-4 self-start">
              <Zap className="h-4 w-4 text-[#4C9AFF]" />
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-200">Rest-Day Split</span>
            </div>
            <MacroChart
              protein={macros.mode !== "manual" && targets.autoMacrosGrams ? targets.autoMacrosGrams.protein : proteinG}
              carb={macros.mode !== "manual" && targets.autoMacrosGrams ? targets.autoMacrosGrams.carb : carbG}
              fat={macros.mode !== "manual" && targets.autoMacrosGrams ? targets.autoMacrosGrams.fat : fatG}
              total={macros.mode !== "manual" ? targets.restEnergy : null}
              size={220} />
            <div className="grid grid-cols-3 gap-2 w-full mt-6 text-center">
              <div><p className="text-[10px] uppercase text-zinc-200">Protein</p><p className="font-display text-lg text-[#4C9AFF]">{macros.mode !== "manual" && targets.autoMacrosGrams ? targets.autoMacrosGrams.protein : proteinG}g</p></div>
              <div><p className="text-[10px] uppercase text-zinc-200">Carbs</p><p className="font-display text-lg text-[#34C759]">{macros.mode !== "manual" && targets.autoMacrosGrams ? targets.autoMacrosGrams.carb : carbG}g</p></div>
              <div><p className="text-[10px] uppercase text-zinc-200">Fat</p><p className="font-display text-lg text-[#FF9500]">{macros.mode !== "manual" && targets.autoMacrosGrams ? targets.autoMacrosGrams.fat : fatG}g</p></div>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
